import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Progress, Space, Spin, Upload } from 'antd';
import { InboxOutlined, StopOutlined } from '@ant-design/icons';
import type { WizardState, WizardAction } from '../wizardReducer';
import { loadFile, loadPdfsViaServer } from '../parsers';
import { buildParsePayload } from '../buildParsePayload';
import styles from './Step1Upload.module.scss';

const { Dragger } = Upload;

// Mirrors CAP-Backend's config.maxImportPdfFiles default (config.ts) — kept in
// sync by hand for now (see PDF_SMART_IMPORT_TODO.md's multi-carrier plan,
// "follow-up" note on exposing this from a config endpoint instead).
const MAX_PDF_FILES = 5;

interface Step1UploadProps {
	state: WizardState;
	dispatch: React.Dispatch<WizardAction>;
	companyId: string;
	payPeriodId: string | null;
	onClose: () => void;
	// Pre-supplied file (e.g. attached in the AI chatbot before this modal was
	// opened) — loaded and auto-advanced past this step on mount.
	initialFile?: File | null;
}

const ACCEPTED = '.csv,.xlsx,.xls,.pdf';

// PDF extraction is a real server round-trip (rasterize + AI vision call) that
// can take anywhere from ~10s to well over a minute for a large scanned
// document — rotating messages keep the wait from reading as a frozen UI.
// Only ticks while a PDF upload is actually in flight (see the `active` param).
const PDF_LOADING_MESSAGES = [
	'Reading pages…',
	'Identifying tables…',
	'Matching employees…',
	'Almost there…',
];

function usePdfLoadingMessage(active: boolean): string {
	const [index, setIndex] = useState(0);

	useEffect(() => {
		if (!active) {
			setIndex(0);
			return;
		}
		const interval = setInterval(() => {
			setIndex((prev) => (prev + 1) % PDF_LOADING_MESSAGES.length);
		}, 3000);
		return () => clearInterval(interval);
	}, [active]);

	return PDF_LOADING_MESSAGES[index];
}

// There's no real incremental progress signal from a single synchronous
// extraction call — this animates toward (but never claims) completion,
// based on elapsed time. PROGRESS_TAU_MS is picked from the Phase 0.3 spike's
// real timing data (a 6-page document took 60-80s): at 30s elapsed this reads
// ~53%, at 60s ~74%, at 90s ~85%, asymptotically approaching PROGRESS_CEILING
// — so it never falsely reads "done" before the request actually resolves.
const PROGRESS_CEILING = 92;
const PROGRESS_TAU_MS = 45000;

function usePdfProgress(active: boolean): number {
	const [percent, setPercent] = useState(0);

	useEffect(() => {
		if (!active) {
			setPercent(0);
			return;
		}
		const start = Date.now();
		const interval = setInterval(() => {
			const elapsed = Date.now() - start;
			setPercent(Math.round(PROGRESS_CEILING * (1 - Math.exp(-elapsed / PROGRESS_TAU_MS))));
		}, 500);
		return () => clearInterval(interval);
	}, [active]);

	return percent;
}

/**
 * Step 1 — Upload. Drag-and-drop / browse, runs detection on the chosen file,
 * shows the split-name banner and parse-error states. Continue advances to
 * Step 2 (WS-9). The detection summary card and sheet switcher are rendered in
 * the Step 2 banner, not here (Functional Spec §3.6, §3.2).
 *
 * PDF uploads (see PDF_SMART_IMPORT_TODO.md) go through a separate async path
 * (loadPdfsViaServer) rather than the synchronous client-side loadFile() used
 * for CSV/XLSX — one or several PDFs (e.g. separate carrier invoices) are
 * sent to the backend together, which merges them and returns the same
 * DetectedStructure shape so Steps 2-4 need no changes at all.
 */
export default function Step1Upload({
	state,
	dispatch,
	companyId,
	payPeriodId,
	onClose,
	initialFile,
}: Step1UploadProps) {
	const [pdfUploading, setPdfUploading] = useState(false);
	const pdfAbortRef = useRef<AbortController | null>(null);
	const pdfLoadingMessage = usePdfLoadingMessage(pdfUploading);
	const pdfProgress = usePdfProgress(pdfUploading);

	const handleCsvOrXlsx = async (file: File, autoAdvance: boolean) => {
		dispatch({ type: 'FILE_SELECTED', file });
		try {
			const detected = await loadFile(file);
			dispatch({ type: 'PARSE_SUCCEEDED', payload: buildParsePayload(detected) });
			if (autoAdvance) dispatch({ type: 'ADVANCE_STEP' });
		} catch (e) {
			const message = e instanceof Error ? e.message : 'Failed to parse file';
			dispatch({ type: 'PARSE_FAILED', error: message });
		}
	};

	// One or several carrier invoices uploaded together (e.g. a health PDF + a
	// life PDF) are merged server-side into one import — see
	// joinAcrossDocuments.ts on the backend. A single-PDF upload is just the
	// N=1 case of this same path.
	const handlePdfBatch = async (files: File[]) => {
		dispatch({ type: 'PDF_FILES_SELECTED', files });

		if (!payPeriodId) {
			dispatch({ type: 'PARSE_FAILED', error: 'Select a pay period before uploading a PDF.' });
			return;
		}

		pdfAbortRef.current?.abort();
		const controller = new AbortController();
		pdfAbortRef.current = controller;
		setPdfUploading(true);

		try {
			const detected = await loadPdfsViaServer(files, companyId, payPeriodId, controller.signal);
			dispatch({ type: 'PARSE_SUCCEEDED', payload: buildParsePayload(detected, detected.extraction) });
		} catch (e: unknown) {
			if (axios.isCancel(e)) {
				// User-initiated cancel — PDF_FILES_SELECTED already cleared prior
				// parse state; nothing else to do.
				return;
			}
			const message = axios.isAxiosError(e)
				? (e.response?.data as { message?: string } | undefined)?.message
				: undefined;
			dispatch({ type: 'PARSE_FAILED', error: message || (e instanceof Error ? e.message : 'Failed to process PDF') });
		} finally {
			setPdfUploading(false);
			pdfAbortRef.current = null;
		}
	};

	const handleFile = async (file: File, autoAdvance = false) => {
		const ext = (file.name.split('.').pop() || '').toLowerCase();
		if (ext === 'pdf') {
			await handlePdfBatch([file]);
			return;
		}
		await handleCsvOrXlsx(file, autoAdvance);
	};

	// Fires once per drag/browse batch (antd calls beforeUpload once per file
	// in the batch — only act on the last one, once the full fileList for this
	// batch is known). A batch of >1 files is only supported when every file
	// is a PDF (multi-carrier import); anything else falls back to the
	// existing single-file handling, unchanged.
	const handleFileList = async (files: File[]) => {
		if (files.length === 0) return;
		if (files.length === 1) {
			await handleFile(files[0]);
			return;
		}
		const allPdf = files.every((f) => (f.name.split('.').pop() || '').toLowerCase() === 'pdf');
		if (!allPdf) {
			dispatch({ type: 'PARSE_FAILED', error: 'Multiple files can only be uploaded together when every file is a PDF.' });
			return;
		}
		if (files.length > MAX_PDF_FILES) {
			dispatch({ type: 'PARSE_FAILED', error: `Upload at most ${MAX_PDF_FILES} PDFs at a time.` });
			return;
		}
		await handlePdfBatch(files);
	};

	const handleCancelPdfUpload = () => {
		pdfAbortRef.current?.abort();
	};

	useEffect(() => {
		if (initialFile && !state.file) {
			void handleFile(initialFile, true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialFile]);

	const canContinue =
		!!state.file && state.parseError === null && state.rawMatrix.length > 0 && !pdfUploading;

	return (
		<div className={styles['step1']}>
			<Dragger
				accept={ACCEPTED}
				multiple
				showUploadList={false}
				disabled={pdfUploading}
				beforeUpload={(file, fileList) => {
					// antd calls beforeUpload once per file in a drag/browse batch —
					// only act once, on the last file, with the full batch in hand.
					// Return false either way to prevent antd's default upload; we
					// parse client-side (CSV/XLSX) or send it ourselves (PDF).
					if (file === fileList[fileList.length - 1]) {
						void handleFileList(fileList as unknown as File[]);
					}
					return false;
				}}
			>
				<p className="ant-upload-drag-icon">
					<InboxOutlined />
				</p>
				<p className="ant-upload-text">
					Drag a payroll file here, or click to browse
				</p>
				<p className="ant-upload-hint">
					Accepts .csv, .xlsx, .xls, and .pdf files. Multiple PDFs (e.g. separate carrier invoices) can be uploaded together.
				</p>
			</Dragger>

			{state.file && !pdfUploading && (
				<p className={styles['step1__filename']}>
					{state.pdfFiles && state.pdfFiles.length > 1 ? (
						<>
							Selected files: <strong>{state.pdfFiles.map((f) => f.name).join(', ')}</strong>
						</>
					) : (
						<>
							Selected file: <strong>{state.file.name}</strong>
						</>
					)}
				</p>
			)}

			{pdfUploading && (
				<Alert
					className={styles['step1__banner']}
					type="info"
					showIcon
					icon={<Spin size="small" />}
					message={pdfLoadingMessage}
					description={
						<div className={styles['step1__banner-body']}>
							<Progress
								percent={pdfProgress}
								size="small"
								strokeColor="#52c41a"
								className={styles['step1__banner-progress']}
							/>
							<span>Reading this PDF can take up to a minute or two for large or multi-page documents.</span>
						</div>
					}
					action={
						<Button
							type="link"
							danger
							icon={<StopOutlined />}
							className={styles['step1__stop-upload-link']}
							onClick={handleCancelPdfUpload}
						>
							Stop uploading file
						</Button>
					}
				/>
			)}

			{/* ENG-383: the split-name toggle moved to Step 2 (near the column
			    mapping grid), where the user can see their columns while deciding —
			    and where it re-evaluates per sheet on a sheet switch. */}

			{state.parseError && (
				<Alert
					className={styles['step1__banner']}
					type="error"
					showIcon
					message="Could not read this file"
					description={state.parseError}
				/>
			)}

			<div className={styles['step1__footer']}>
				<Space>
					<Button className={styles['step1__btn-secondary']} onClick={onClose}>
						Cancel
					</Button>
					<Button
						type="primary"
						disabled={!canContinue}
						onClick={() => dispatch({ type: 'ADVANCE_STEP' })}
						className="btn-black"
						style={{ height: '41px' }}
					>
						Continue
					</Button>
				</Space>
			</div>
		</div>
	);
}
